import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, FileText, Building2, Briefcase, User } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";

export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const box = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(async () => {
      try { setRes((await api.get("/admin/search", { params: { q } })).data); setOpen(true); } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (path) => { setOpen(false); setQ(""); nav(path); };
  const groups = res ? [
    { key: "reports", label: "Reports", icon: FileText, items: res.reports.map((r) => ({ id: r.id, title: `#${r.report_number ?? "—"} · ${r.employee_name} · ${fmtDate(r.report_date)}`, sub: r.comments, path: `/admin/reports/${r.id}` })) },
    { key: "projects", label: "Projects", icon: Briefcase, items: res.projects.map((p) => ({ id: p.id, title: p.name, sub: `Job # ${p.job_number || "—"}`, path: `/admin/projects/${p.id}` })) },
    { key: "clients", label: "Clients", icon: Building2, items: res.clients.map((c) => ({ id: c.id, title: c.name, sub: c.status, path: `/admin/clients/${c.id}` })) },
    { key: "employees", label: "Employees", icon: User, items: res.employees.map((u) => ({ id: u.id, title: u.name, sub: u.email, path: `/admin/reports?employee_id=${u.id}` })) },
  ].filter((g) => g.items.length) : [];

  return (
    <div className="relative w-full max-w-xl" ref={box}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => res && setOpen(true)}
        placeholder="Search employees, clients, projects, job #, notes…" data-testid="global-search-input"
        className="h-10 w-full rounded-md border border-border bg-slate-50 pl-9 pr-3 text-sm focus:border-[#5C241E] focus:bg-white focus:outline-none" />
      {open && res && (
        <div className="absolute left-0 right-0 top-12 z-50 max-h-[70vh] overflow-auto rounded-md border border-border bg-white shadow-xl" data-testid="global-search-results">
          {!groups.length && <p className="p-4 text-sm text-slate-500">No matches for “{q}”.</p>}
          {groups.map((g) => (
            <div key={g.key} className="border-b border-border last:border-0">
              <p className="eyebrow px-4 pb-1 pt-3">{g.label}</p>
              {g.items.map((it) => (
                <button key={it.id} onClick={() => go(it.path)} data-testid={`search-result-${g.key}-${it.id}`}
                  className="flex w-full items-start gap-3 px-4 py-2 text-left hover:bg-[#5C241E]/5">
                  <g.icon className="mt-0.5 h-4 w-4 shrink-0 text-[#5C241E]" />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-slate-900">{it.title}</span>
                    {it.sub && <span className="block truncate text-xs text-slate-500">{it.sub}</span>}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
